<script lang="ts" setup>
import { EvaluationRelevantInstance } from "types/EvaluationResults";
import { useSystemCommentsStore } from "~/composables/useSystemCommentsStore";

import { Delta } from "@vueup/vue-quill";
import { ChecklistCriteria, CriteriaInstance } from "types/Checklist";

const props = defineProps({
  criteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: false,
  },
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteriaConfig: {
    type: Object as PropType<EvaluationCriteriaConfiguration>,
    required: true,
  },
  relevantInstance: {
    type: Object as PropType<EvaluationRelevantInstance>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },

  isExpanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["highlightAll"]);

const documentId = inject("documentId") as any;

const label = computed(() => {
  if (!props.criteriaConfig.instanceLabelOverride) {
    return relevantInstanceDisplayText.value;
  }

  const number = props.relevantInstance.position ?? props.index + 1;

  return `${props.criteriaConfig.instanceLabelOverride} ${number}`;
});

const relevantInstanceDisplayText = computed(() => {
  const positions = relevantInstancesPositions.value;

  let text = "";

  for (const position of positions) {
    const tokenStore = useParagraphTokenStore(documentId, position);
    text += tokenStore.token?.text ?? "";
  }

  return text;
});
const systemCommentsStore = useSystemCommentsStore();
const { filteredComments } = storeToRefs(systemCommentsStore);

const comments = computed(() => {
  if (!props.criteria) return [];

  if (!props.criteria.id) return [];

  if (!props.criteria.quickCommentTag)
    return props.criteria.quickComments ?? [];

  return filteredComments
    .value({
      documentId: documentId,
      tag: props.criteria.quickCommentTag,
    })
    .map((comment: any) => comment.comment);
});

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const { setSelection } = useDocumentSelectionStore(documentId);

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const createAnnotation = () => {
  // First I need to set the selection to this relevant instances tokens
  // So first I need the start and end global positions for this relevant instance

  props.relevantInstance.tokens?.sort((a, b) => a.position - b.position);
  const startId = props.relevantInstance.tokens?.[0].id;
  let startGlobalPosition = tokenGlobalPositionFromId.value(startId);
  const endId =
    props.relevantInstance.tokens?.[props.relevantInstance.tokens.length - 1]
      .id;

  let endGlobalPosition = tokenGlobalPositionFromId.value(endId);

  if (props.criteria?.insertCommentAtMarker == true) {
    const startTokenStore = useParagraphTokenStore(
      documentId,
      startGlobalPosition
    );

    const paragraphId = startTokenStore.paragraphId;
    const startToken = startTokenStore.token;

    if (!paragraphId || !startToken) return;

    const paragraphStore = useDocumentParagraphStore(documentId, paragraphId);
    const closestMarker = paragraphStore.getClosestMarkerToGlobalPosition(
      startToken.position
    );

    if (closestMarker != undefined) {
      const markerGlobalPosition = tokenGlobalPositionFromId.value(
        closestMarker.id
      );

      startGlobalPosition = markerGlobalPosition;
      endGlobalPosition = markerGlobalPosition;
    }
  }

  setSelection(startGlobalPosition, endGlobalPosition);

  return createDefaultAnnotation();
};

const annotateInstances = () => {
  const commentOnlyAnnotation = createAnnotation();

  if (commentOnlyAnnotation === undefined) return;

  if (props.criteria?.useSmartAnnotations) {
    commentOnlyAnnotation.isSmartAnnotation = true;
  }

  commentOnlyAnnotation.checklistCriteriaId = props.criteria?.id;

  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const addAnnotation = (comment: string) => {
  const commentOnlyAnnotation = createAnnotation();

  if (commentOnlyAnnotation == undefined) return;

  commentOnlyAnnotation.commentObject = new Delta().insert(comment).ops;
  commentOnlyAnnotation.quickComment = comment;

  if (props.criteria?.useSmartAnnotations) {
    commentOnlyAnnotation.isSmartAnnotation = true;
  }

  commentOnlyAnnotation.checklistCriteriaId = props.criteria?.id;

  saveAnnotation(
    commentOnlyAnnotation,
    useIsAnnotationCommentLong(commentOnlyAnnotation)
  ).then();

  setSelection(undefined, undefined);
};

const relevantInstancesPositions = computed(() => {
  const startId = props.relevantInstance.tokens?.[0].id;
  const endId =
    props.relevantInstance.tokens?.[props.relevantInstance.tokens.length - 1]
      .id;
  let startGlobalPosition = tokenGlobalPositionFromId.value(startId);
  let endGlobalPosition = tokenGlobalPositionFromId.value(endId);

  if (props.relevantInstance.displayRange) {
    // ok I need to convert a token index to a position somehow haha. Fuck me haha hwo do I do that?
    startGlobalPosition = useDocumentTokenIdPositionMapStore(
      documentId
    ).tokenGlobalPositionFromId(
      props.relevantInstance.displayRange.startTokenId
    );
    endGlobalPosition = useDocumentTokenIdPositionMapStore(
      documentId
    ).tokenGlobalPositionFromId(props.relevantInstance.displayRange.endTokenId);
  }

  const positions = [];
  for (let i = startGlobalPosition; i <= endGlobalPosition; i++) {
    positions.push(i);
  }

  return positions;
});

const tokenFromPosition = computed(() => {
  return (tokenGlobalPosition: number) => {
    const tokenStore = useParagraphTokenStore(documentId, tokenGlobalPosition);
    return tokenStore.token;
  };
});

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const appUserData = useAppUserDataStore();
const { isUserProductReviewer } = storeToRefs(appUserData);
</script>

<template>
  <div class="flex flex-row items-start grow mr-2">
    <BaseIconButton
      v-if="
        isTeacherViewMode &&
        relevantInstance?.criteria?.length > 0 &&
        criteriaConfig.forceOpen != true
      "
      class="text-[22px] p-[2px]"
      :class="{
        hidden: criteriaConfig.isTopLevelCriteria && false,
      }"
    >
      <icon
        :class="
          isExpanded
            ? 'rotate-90 -translate-y-[1px]'
            : 'rotate-0 -translate-y-[1px]'
        "
        name="mdi:chevron-right"
      />
    </BaseIconButton>

    <span
      v-if="
        isTeacherViewMode &&
        criteriaConfig.highlightInstances == true &&
        comments &&
        comments.length > 0
      "
    >
      <BasePopover
        :delay="{ show: 100, hide: 500 }"
        :disabled="!comments || comments.length == 0"
        :show-trigger-methods="['hover']"
        :hide-trigger-methods="['hover']"
        side="left"
        align="end"
      >
        <BaseIconButton
          :data-step-id="`quick-comment-button-${relevantInstance.location}`"
          @click.stop="annotateInstances"
        >
          <BaseIconCommentBox />
        </BaseIconButton>
        <template #content>
          <div class="flex flex-col p-1 bg-surface rounded max-w-[400px]">
            <span
              v-for="(comment, index) in comments"
              :key="index"
              class="p-2 cursor-pointer hover:bg-light-default rounded"
              @mousedown.prevent
              @click="addAnnotation(comment)"
              v-close-popper
            >
              {{ comment }}
            </span>
          </div>
        </template>
      </BasePopover>
    </span>
    <span
      v-if="
        isTeacherViewMode &&
        relevantInstance.details &&
        relevantInstance.details.type &&
        relevantInstance.details.reason &&
        isUserProductReviewer
      "
    >
      <BasePopover>
        <BaseIconButton>
          <icon name="material-symbols:info" />
        </BaseIconButton>
        <template #content>
          <div class="flex flex-col p-1 max-w-[350px]">
            <span class="font-bold mb-1">
              {{ relevantInstance.details.type }}
            </span>
            <span>
              {{ relevantInstance.details.reason }}
            </span>
          </div>
        </template>
      </BasePopover>
    </span>
    <span
      v-if="criteriaConfig.preserveInstanceFormatting != true"
      class="font-bold select-none flex flex-row items-center -mt-[1px] leading-[30px]"
      :class="{ 'mx-2': !criteriaConfig.isTopLevelCriteria || true }"
    >
      {{ label }}
    </span>
    <p
      v-if="criteriaConfig.preserveInstanceFormatting == true"
      class="select-none flex flex-row flex-wrap items-center -mt-[1px]"
    >
      <DocumentTokenStyle
        v-for="tokenPosition in relevantInstancesPositions"
        :key="tokenPosition"
        :token="tokenFromPosition(tokenPosition)"
        class="leading-[30px]"
      />
    </p>
  </div>
</template>

<style scoped></style>
